'use client';

import { ReactNode, useEffect, useState } from 'react';
import { useLocale, useMessages, NextIntlClientProvider } from 'next-intl';
import { type Locale } from '@/i18n/config';
import { getLocaleFileName } from '@/i18n/locale-file-mapper';
import { onI18nError, setInitialMessages } from '@/i18n/on-i18n-error';
import logger from '@/utils/client-logger';

/**
 * I18n 预加载 Provider
 *
 * 在 NextIntlClientProvider 内部包裹此 provider
 * 用于非阻塞地预加载所有语言包
 *
 * 工作原理：
 * 1. 首屏加载只发送初始路由的语言包（快速）
 * 2. 组件挂载时，使用 requestIdleCallback 异步加载完整语言包
 * 3. 加载完成后通过 setState 触发 React 重渲染
 * 4. 新的 NextIntlClientProvider 注入完整消息，所有子组件自动获取完整翻译
 */
export function I18nPreloadProvider({ children }: { children: ReactNode }) {
  const locale = useLocale();
  const initialMessages = useMessages() as Record<string, any>;
  const [fullMessages, setFullMessages] = useState<Record<string, any> | null>(null);

  // 注册初始 messages 的 namespace 集合，用于后续 useTranslations 的兜底检测
  setInitialMessages(initialMessages);

  useEffect(() => {
    const preloadMessages = async () => {
      try {
        const siteName = process.env.NEXT_PUBLIC_SITE_NAME;
        const localeFileName = getLocaleFileName(locale as Locale, siteName);
        const loaded = (await import(`@/locales/${localeFileName}.json`)).default;
        setFullMessages(loaded);
        logger.debug(
          `[i18n] Preloaded full messages for locale: ${locale} (file: ${localeFileName})`,
        );
      } catch (error) {
        logger.warn(`[i18n] Failed to preload messages for ${locale}:`, error);
      }
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      requestIdleCallback(() => preloadMessages(), { timeout: 5000 });
    } else {
      setTimeout(preloadMessages, 0);
    }
  }, [locale]);

  const messages = fullMessages ?? initialMessages;

  return (
    <NextIntlClientProvider locale={locale} messages={messages} onError={onI18nError}>
      {children}
    </NextIntlClientProvider>
  );
}
